/*
 * hanlyn-launch-gate.js
 * ---------------------
 * Companion to snippets/hanlyn-launch-gate.liquid.
 *
 * The gate itself is decided SERVER SIDE, against Shopify's clock, every time a
 * page renders. That is what makes the badge and the disabled buy button
 * disappear on their own at the launch instant with nothing to untag.
 *
 * This file only covers the gaps a server-rendered gate cannot close:
 *
 *   1. A page that was rendered before the launch instant and is still sitting
 *      in the CDN cache (or in a back/forward-cached tab) when a shopper opens
 *      it after the instant. It would show a stale "coming soon" state.
 *   2. A tab left open across the launch instant -- a 1am drop is exactly the
 *      case where people camp on the page waiting.
 *   3. (Added 2026-10-21, v2.26.) Dawn's variant picker rewrites the buy
 *      button on every size change: it sets the label back to "Add to cart"
 *      and re-enables it when the variant has stock. On a pre-launch product
 *      with stock (or with oversell allowed) that turned the gated button into
 *      a working one, both on the product page and in the quick shop drawer,
 *      which clones the product form. The lock below keeps every gated form's
 *      submit button disabled and labelled, and a capture-phase guard refuses
 *      any add that still gets through. A form is gated when it carries the
 *      server-rendered .hanlyn-launch-note (that note travels with the form
 *      into the quick shop drawer) or sits inside a [data-hanlyn-launch] host.
 *
 * In cases 1 and 2 the honest fix is a reload: the real add-to-cart markup is
 * server rendered and cannot be faked up client side. Reloads are capped at one
 * per product per session so a shopper with a badly-set device clock sees a
 * single reload, never a loop.
 *
 * It also suppresses the third-party pre-order app's own coming-soon badge on
 * any card this system has already badged, so a product carried by both never
 * shows two.
 */
(function () {
  'use strict';

  // Both card-product.liquid and buy-buttons.liquid ask for this file, and a
  // page can carry both. Run once.
  if (window.__hanlynLaunchGate) return;
  window.__hanlynLaunchGate = true;

  var SELECTOR = '[data-hanlyn-launch]';
  var RELOAD_KEY = 'hanlynLaunchReloaded:';
  var timers = [];

  function alreadyReloaded(key) {
    try {
      return window.sessionStorage.getItem(RELOAD_KEY + key) === '1';
    } catch (e) {
      // Private mode, or storage blocked. Treat as "already reloaded" so a
      // browser that cannot remember the reload can never loop on it.
      return true;
    }
  }

  function markReloaded(key) {
    try {
      window.sessionStorage.setItem(RELOAD_KEY + key, '1');
    } catch (e) {
      /* nothing we can do, and nothing worth breaking the page over */
    }
  }

  /* 2026-10-22: one reload per page path at most. It used to be one per product, so a cached
     collection page full of launched products could reload again and again as you scrolled,
     throwing shoppers back to the top. */
  function refresh() {
    var key = 'page:' + window.location.pathname;
    if (alreadyReloaded(key)) return;
    markReloaded(key);
    window.location.reload();
  }

  /* A card whose launch time has passed but whose markup still says coming soon (it came from a
     cached page or a cached infinite-scroll batch). Don't reload for it; just drop the stale
     badge and leave the card usable. */
  function expire(el) {
    if (el.__hanlynExpired) return;
    el.__hanlynExpired = true;
    el.classList.add('hanlyn-launch-expired');
    el.classList.remove('hc-prelaunch');
    var badges = el.querySelectorAll('.hanlyn-launch-badge');
    for (var i = 0; i < badges.length; i++) badges[i].style.display = 'none';
  }

  /* Hide the pre-order app's badge on cards this system has already badged. */
  function dedupeAppBadges(root) {
    var gated = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < gated.length; i++) {
      var card = gated[i].closest('.card-wrapper, .card, product-info, .product');
      if (!card) continue;
      var stale = card.querySelectorAll('.comingSoonBadge2, [class*="comingSoonBadge"]');
      for (var j = 0; j < stale.length; j++) {
        stale[j].style.display = 'none';
      }
    }
  }

  function arm(el, initial) {
    if (el.__hanlynArmed || el.__hanlynExpired) return;
    var iso = el.getAttribute('data-launch-iso');
    // No ISO means the launch tag could not be parsed and the product is gated
    // generically. There is no instant to count down to, and it must stay
    // gated until someone fixes the tag.
    if (!iso) return;

    var when = Date.parse(iso);
    if (isNaN(when)) return;

    var key = el.getAttribute('data-launch-key') || iso;
    var delta = when - Date.now();

    if (delta <= 0) {
      // Server said pre-launch, browser says the moment has passed: the markup is
      // stale. Reload once only for the page as first rendered; anything added later
      // (infinite scroll, quick shop) is just cleaned up in place.
      if (initial && !alreadyReloaded('page:' + window.location.pathname)) { refresh(); return; }
      expire(el);
      return;
    }

    el.__hanlynArmed = true;

    // setTimeout saturates past ~24.8 days, so only arm timers inside a day and
    // re-check hourly beyond that.
    if (delta > 86400000) {
      timers.push(window.setTimeout(function () { el.__hanlynArmed = false; arm(el, true); }, 3600000));
      return;
    }

    timers.push(window.setTimeout(function () { refresh(); }, delta + 1000));
  }

  /* ---- Buy-button lock (case 3) ------------------------------------------ */

  // The form root a launch note or host belongs to.
  function gatedForms(root) {
    var found = [];
    var notes = (root || document).querySelectorAll('.hanlyn-launch-note');
    for (var i = 0; i < notes.length; i++) {
      var host = notes[i].closest(SELECTOR);
      if (host && host.__hanlynExpired) continue;
      var f = notes[i].closest('product-form') || notes[i].closest('form');
      if (f && found.indexOf(f) === -1) found.push(f);
    }
    var hosts = (root || document).querySelectorAll(SELECTOR);
    for (var j = 0; j < hosts.length; j++) {
      if (hosts[j].__hanlynExpired) continue;
      var forms = hosts[j].querySelectorAll('product-form');
      for (var k = 0; k < forms.length; k++) {
        if (found.indexOf(forms[k]) === -1) found.push(forms[k]);
      }
    }
    return found;
  }

  function isGated(node) {
    if (!node || typeof node.closest !== 'function') return false;
    var scope = node.closest('product-form') || node.closest('form');
    if (!scope) return false;
    if (scope.querySelector('.hanlyn-launch-note')) return true;
    return !!scope.closest(SELECTOR);
  }

  function shortLabel(form, button) {
    if (button.getAttribute('data-hanlyn-launch-label')) {
      return button.getAttribute('data-hanlyn-launch-label');
    }
    var current = (button.textContent || '').replace(/\s+/g, ' ').trim();
    var label = '';
    if (/^available /i.test(current) || /^coming soon/i.test(current)) {
      label = current;
    } else {
      var note = form.querySelector('.hanlyn-launch-note');
      var text = note ? (note.textContent || '').replace(/\s+/g, ' ').trim() : '';
      label = text ? text.replace(/\s+at\s+.*$/i, '') : 'Coming soon';
    }
    button.setAttribute('data-hanlyn-launch-label', label);
    return label;
  }

  function lockForm(form) {
    var buttons = form.querySelectorAll('[type="submit"], button[name="add"]');
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      var label = shortLabel(form, b);
      if (!b.disabled) b.disabled = true;
      if (b.getAttribute('aria-disabled') !== 'true') b.setAttribute('aria-disabled', 'true');
      // Dawn writes the label into the button's first <span>; keep that span
      // when it exists so the loading spinner markup is left alone.
      var span = b.querySelector('span:not(.loading__spinner):not(.visually-hidden)');
      var target = span || b;
      if ((target.textContent || '').replace(/\s+/g, ' ').trim() !== label) {
        target.textContent = label;
      }
    }
  }

  function lockAll(root) {
    var forms = gatedForms(root);
    for (var i = 0; i < forms.length; i++) lockForm(forms[i]);
  }

  // Last line of defence: refuse any add-to-cart from a gated form, whatever
  // state the button is in. Capture phase on document runs before Dawn's own
  // submit handler on the form.
  function guard(event) {
    var t = event.target;
    var node = event.type === 'submit' ? t : (t && t.closest ? t.closest('[type="submit"], button[name="add"]') : null);
    if (!node || !isGated(node)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var scope = node.closest('product-form') || node.closest('form');
    if (scope) lockForm(scope);
  }

  function scan(root, initial) {
    var els = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) arm(els[i], initial);
    dedupeAppBadges(root);
    lockAll(root);
  }

  function init() {
    document.addEventListener('submit', guard, true);
    document.addEventListener('click', guard, true);

    scan(document, true);

    // Collection filtering swaps #ProductGridContainer wholesale, the quick
    // shop drawer injects scraped markup, and Dawn rewrites the buy button on
    // every variant change, so re-scan on any of those. The callback only
    // writes when something actually differs, so it cannot feed itself.
    if (window.MutationObserver) {
      var pending = false;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          scan(document);
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    // A tab restored from the back/forward cache never re-runs its scripts and
    // never re-renders on the server, so re-check on show.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) scan(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
